import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Empty, Space, Switch, Table, Tag, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import {
    BillingDocumentType,
    OrganizationBillingDocumentType
} from '../store/user/types';
import {
    getSalesDocumentBillingDocumentTypesAPI,
    getSalesDocumentOrganizationBillingDocumentTypesAPI,
    postSalesDocumentOrganizationBillingDocumentTypeAPI,
    putSalesDocumentOrganizationBillingDocumentTypeAPI
} from '../store/user/restCallUser';

const { Text } = Typography;

interface BillingDocumentTypesManagementProps {
    organizationId: string;
    organizationName?: string;
    onChanged?: () => Promise<void> | void;
}

interface BillingDocumentTypeRow extends BillingDocumentType {
    organizationType?: OrganizationBillingDocumentType;
}

function responseData(response: any): any {
    return response?.Response?.data ?? response?.Response ?? response?.data ?? [];
}

function responseError(response: any): string | null {
    if (!response || response.Error !== null || response.StatusCode < 200 || response.StatusCode >= 300) {
        return response?.Error?.message || response?.Error || 'No se pudo completar la operación.';
    }
    return null;
}

function BillingDocumentTypesManagement({
    organizationId,
    organizationName,
    onChanged
}: BillingDocumentTypesManagementProps) {
    const [catalog, setCatalog] = useState<BillingDocumentType[]>([]);
    const [organizationTypes, setOrganizationTypes] = useState<OrganizationBillingDocumentType[]>([]);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!organizationId) return;
        setLoading(true);
        setError(null);
        try {
            const [catalogResponse, organizationResponse] = await Promise.all([
                getSalesDocumentBillingDocumentTypesAPI(),
                getSalesDocumentOrganizationBillingDocumentTypesAPI(organizationId)
            ]);
            const catalogError = responseError(catalogResponse);
            const organizationError = responseError(organizationResponse);
            if (catalogError || organizationError) {
                throw new Error(catalogError || organizationError || 'No se pudo cargar el catálogo.');
            }
            setCatalog(responseData(catalogResponse) as BillingDocumentType[]);
            setOrganizationTypes(responseData(organizationResponse) as OrganizationBillingDocumentType[]);
        } catch (loadError: any) {
            setError(loadError?.message || 'No se pudo cargar la configuración de comprobantes.');
        } finally {
            setLoading(false);
        }
    }, [organizationId]);

    useEffect(() => {
        void load();
    }, [load]);

    const rows = useMemo<BillingDocumentTypeRow[]>(() => catalog.map(item => ({
        ...item,
        organizationType: organizationTypes.find(
            organizationType => organizationType.billingDocumentTypeId === item.id
        )
    })), [catalog, organizationTypes]);

    const updateStatus = async (row: BillingDocumentTypeRow, enabled: boolean) => {
        setSavingId(row.id);
        setError(null);
        try {
            const status = enabled ? 'ACTIVO' : 'INACTIVO';
            const response = row.organizationType
                ? await putSalesDocumentOrganizationBillingDocumentTypeAPI(
                    organizationId,
                    row.organizationType.id,
                    { billingDocumentTypeId: row.id, status }
                )
                : await postSalesDocumentOrganizationBillingDocumentTypeAPI(
                    organizationId,
                    { billingDocumentTypeId: row.id, status }
                );
            const operationError = responseError(response);
            if (operationError) throw new Error(operationError);
            message.success(enabled
                ? `${row.name} habilitado para la organización.`
                : `${row.name} deshabilitado para la organización.`);
            await load();
            await onChanged?.();
        } catch (saveError: any) {
            setError(saveError?.message || 'No se pudo actualizar el tipo de comprobante.');
        } finally {
            setSavingId(null);
        }
    };

    return (
        <Card
            title="Tipos de comprobante"
            extra={<Button type="link" icon={<ReloadOutlined />} onClick={() => void load()}>Actualizar</Button>}
            style={{ marginBottom: 16 }}
        >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Alert
                    type="info"
                    showIcon
                    message="Módulo Blue"
                    description={`Habilita los comprobantes que ${organizationName || 'la organización'} podrá usar en sus puntos de emisión. Deshabilitar no elimina la configuración ni el historial.`}
                />
                {error && <Alert type="error" showIcon message={error} />}
                {!loading && rows.length === 0 ? (
                    <Empty description="No hay tipos de comprobante activos en el catálogo global." />
                ) : (
                    <Table<BillingDocumentTypeRow>
                        rowKey="id"
                        loading={loading}
                        pagination={false}
                        dataSource={rows}
                        columns={[
                            { title: 'Código', dataIndex: 'code', width: 130 },
                            { title: 'Nombre', dataIndex: 'name' },
                            { title: 'Código SRI', dataIndex: 'sriCode', width: 130 },
                            {
                                title: 'Estado en la organización',
                                width: 220,
                                render: (_, row) => (
                                    <Space>
                                        <Switch
                                            checked={row.organizationType?.status === 'ACTIVO'}
                                            loading={savingId === row.id}
                                            onChange={checked => void updateStatus(row, checked)}
                                        />
                                        <Tag color={row.organizationType?.status === 'ACTIVO' ? 'green' : 'default'}>
                                            {row.organizationType?.status === 'ACTIVO' ? 'HABILITADO' : 'NO HABILITADO'}
                                        </Tag>
                                    </Space>
                                )
                            }
                        ]}
                    />
                )}
                <Text type="secondary">
                    La factura usa el código SRI 01; notas de crédito y débito se habilitan solo cuando la organización las necesite.
                </Text>
            </Space>
        </Card>
    );
}

export default BillingDocumentTypesManagement;
